/**
 * iter90 — Shared spend-error toast mapper for the Connections module.
 *
 * Deduplicates the 429/402 error handling between GraphWorkspace and AICopilot
 * so future gate additions live in one place.
 */
import { toast } from 'sonner';

/**
 * Map a 429/402 axios error to a user-friendly toast.
 *
 * @param {import('axios').AxiosError} err - The error thrown by axios.
 * @param {(path: string) => void} navigate - react-router navigate function.
 * @param {string} [defaultMessage] - Fallback toast text for anything unexpected.
 */
export function handleSpendError(err, navigate, defaultMessage = 'Request failed') {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  const info = (detail && typeof detail === 'object') ? detail : {};

  if (status === 429) {
    if (info.code === 'cooldown') {
      const mins = info.retry_after_seconds
        ? Math.ceil(info.retry_after_seconds / 60)
        : null;
      toast.error(mins
        ? `Cooldown: wait ${mins} more min. (limit protects your AI credits)`
        : (info.message || 'Cooldown active — try again shortly.'));
      return;
    }
    if (info.code === 'hourly_rate') {
      const used = info.used_hour ?? '?';
      const limit = info.hour_limit ?? info.limit ?? '?';
      toast.error(`Rate limit: ${used}/${limit} used this hour. Try again shortly.`);
      return;
    }
    if (info.code === 'daily_cap') {
      const used = info.used_today ?? '?';
      const cap = info.cap ?? info.day_limit ?? info.limit ?? '?';
      toast.error(`Daily limit reached (${used}/${cap}). Resets at 00:00 UTC.`);
      return;
    }
    toast.error(info.message || 'Rate limit hit — please try again shortly.');
    return;
  }

  if (status === 402) {
    toast.error(
      info.message || 'AI credits exhausted for this month. Upgrade or buy more credits.',
      {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/dashboard/subscription'),
        },
        duration: 8000,
      }
    );
    return;
  }

  toast.error((typeof detail === 'string' && detail) || defaultMessage);
}
